import type { CaseImageRecord } from '@/api/caseImages'
import type { CaseImageVariantsById } from '@/utils/caseImages/groupCaseImageVariants'
import {
  resolveCaseImageDisplayUrl,
  resolveSourceImageUrl,
} from '@/utils/caseImages/resolveCaseImageDisplayUrl'
import { isImagePlaceholder } from '@/utils/editor/imagePlaceholder'
import {
  imagePath,
  resolveCaseImageForItem,
  type CaseImageItemRef,
} from '@/utils/editor/resolveCaseImageForItem'

/** The parts of an editor image item that say whether AI edited what it shows. */
export type EditedWithAiItem = CaseImageItemRef & {
  image?: { url?: string; aiVariantId?: string; editedUrl?: string }
}

/**
 * True when the file a case image is shown as is an accepted enhancement
 * nobody reverted. The backend's `displayUrl` comes from the shared display
 * rule (`pickCaseImageDisplayUrl` in `@messenger/utils`): the shown upscale
 * when there is one, otherwise the photo itself (`mioUrl || originalUrl`,
 * which `resolveSourceImageUrl` reads through the same rule). So the display
 * file differs from the photo itself exactly while an accepted, unreverted
 * enhancement is shown, in the editor, in print and for everyone alike.
 */
export const caseImageShowsEnhancement = (image: CaseImageRecord): boolean =>
  imagePath(image.displayUrl) !== imagePath(resolveSourceImageUrl(image))

/**
 * Whether a canvas image item shows a photo AI edited, so it carries the
 * "Edited with AI" tag. Both kinds of edit count the same:
 *
 * - a background removal: the item carries the cutout's `aiVariantId`,
 *   which every write that renders from the source photo clears
 *   (`syncImageEditToEditorPages`);
 * - an enhancement: the item shows its case photo's display file while that
 *   file is an accepted, unreverted enhancement. A template placeholder shows
 *   its slot's display file; any other item must point at that file itself,
 *   so an item still holding an older file, or the original after a revert,
 *   carries no tag.
 *
 * Without the case's images loaded, or for an item that shows no case photo
 * (a logo, a design asset), only a cutout counts.
 */
export const isItemEditedWithAi = (
  caseImages: CaseImageRecord[],
  item: EditedWithAiItem | null | undefined,
): boolean => {
  if (!item) return false
  if (item.image?.aiVariantId) return true
  const caseImage = resolveCaseImageForItem(caseImages, item)
  return caseImage !== null && itemShowsEnhancement(caseImage, item)
}

/**
 * Whether an item showing this case photo shows its accepted, unreverted
 * enhancement: the photo's display file is an enhancement, and the item is
 * a template placeholder (it shows its slot's display file) or points at
 * that file itself.
 */
const itemShowsEnhancement = (
  caseImage: CaseImageRecord,
  item: EditedWithAiItem,
): boolean => {
  if (!caseImageShowsEnhancement(caseImage)) return false
  const url = item.image?.url
  if (!url) return false
  if (isImagePlaceholder(url)) return true
  return imagePath(url) === imagePath(caseImage.displayUrl)
}

/** What a canvas photo's toolbar offers for its photo's enhancement. */
export type ItemEnhancementAction = {
  /**
   * `revert` while the item shows an accepted enhancement ("Revert
   * changes"); `showEnhancement` while its photo shows the original because
   * the enhancement was reverted ("Show enhancement").
   */
  kind: 'revert' | 'showEnhancement'
  /** The case photo the action saves the revert mark on. */
  caseImageId: string
}

/**
 * The enhancement action a canvas photo offers, or null when it offers none.
 * Both actions change the photo for everyone (the server's revert mark), so
 * they are offered only on an item the change moves:
 *
 * - `revert` exactly where the item carries the "Edited with AI" tag for an
 *   enhancement (`isItemEditedWithAi`'s enhancement case);
 * - `showEnhancement` on an item showing the photo itself while the photo's
 *   newest accepted enhancement is reverted, read off the case's variants
 *   (`resolveCaseImageDisplayUrl`), so nothing is offered before they load.
 *
 * An item showing a background removal (`aiVariantId`) offers neither: a
 * revert does not move it, and its way back is "Use original photo" in the
 * Remove Background tool. Nor does an item showing a Pintura render
 * (`editedUrl`), which a revert leaves in place (`itemsToSwapToEnhancedImage`).
 */
export const enhancementActionForItem = (
  caseImages: CaseImageRecord[],
  variantsById: CaseImageVariantsById,
  item: EditedWithAiItem | null | undefined,
): ItemEnhancementAction | null => {
  if (!item?.image?.url || item.image.aiVariantId || item.image.editedUrl) {
    return null
  }
  const caseImage = resolveCaseImageForItem(caseImages, item)
  if (!caseImage) return null
  if (caseImageShowsEnhancement(caseImage)) {
    return itemShowsEnhancement(caseImage, item)
      ? { kind: 'revert', caseImageId: caseImage.id }
      : null
  }
  const { reverted } = resolveCaseImageDisplayUrl({
    image: caseImage,
    variants: variantsById[caseImage.id] ?? [],
  })
  return reverted
    ? { kind: 'showEnhancement', caseImageId: caseImage.id }
    : null
}
